import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { MST4010_R01Dto } from 'src/containers/mes/MST/MST4010_R01/Dto_MST4010_R01';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';

import { useMST4010_R01Store } from './Store_MST4010_R01';
import { cellDateComparator, onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'MST4010_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST4010_R01Dto> | null>;
	defaultMST4010_R01Row: MST4010_R01Dto;
}

export const Table_MST4010_R01 = (props: Props) => {
	const { gridRef, defaultMST4010_R01Row } = props;

	const {
		MST4010_R01List,
		selectedMST4010_R01,
		setSelectedMST4010_R01,
		initSelectedMST4010_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useMST4010_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'equipmentCd',
		gridData: MST4010_R01List,
		defaultGridData: defaultMST4010_R01Row,
		selectedRow: selectedMST4010_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		equipmentCd: '설비코드',
		equipmentNm: '설비명',
		equipmentType: '설비유형',
		equipmentSpec: '규격',
		equipmentSeq: '설비순서',
		manufacturerNm: '제조사',
		compNm: '사업장',
		eqmOrgNm: '관리부서',
		eqmPersonNm: '담당자',
		purchaseDt: '구입일자',
		eqmStatus: '상태',
		disposalDt: '폐기일자',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'equipmentCd')(params), []);

	const baseColumnDefs: ColDef<MST4010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '설비코드', colId: 'equipmentCd', field: 'equipmentCd', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'equipmentCd'),
		},
		{
			headerName: '설비명', colId: 'equipmentNm', field: 'equipmentNm', width: 220,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'equipmentNm'),
		},
		{
			headerName: '설비유형', colId: 'equipmentType', field: 'equipmentType', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'equipmentType'),
		},
		{
			headerName: '규격', colId: 'equipmentSpec', field: 'equipmentSpec', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'equipmentSpec'),
		},
		{
			headerName: '설비순서', colId: 'equipmentSeq', field: 'equipmentSeq', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'equipmentSeq'),
		},
		{
			headerName: '제조사', colId: 'manufacturerNm', field: 'manufacturerNm', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'manufacturerNm'),
		},
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
		},
		{
			headerName: '관리부서', colId: 'eqmOrgNm', field: 'eqmOrgNm', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'eqmOrgNm'),
		},
		{
			headerName: '담당자', colId: 'eqmPersonNm', field: 'eqmPersonNm', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'eqmPersonNm'),
		},
		{
			headerName: '구입일자', colId: 'purchaseDt', field: 'purchaseDt', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'purchaseDt'),
			valueFormatter: (p) => p.data?.purchaseDt ? formatDate(new Date(p.data.purchaseDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '상태', colId: 'eqmStatusNm', field: 'eqmStatusNm', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'eqmStatusNm'),
		},
		{
			headerName: '폐기일자', colId: 'disposalDt', field: 'disposalDt', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'disposalDt'),
			valueFormatter: (p) => p.data?.disposalDt ? formatDate(new Date(p.data.disposalDt)) : '',
			comparator: cellDateComparator,
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<MST4010_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST4010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<MST4010_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedMST4010_R01?.equipmentCd === rowData.equipmentCd;
		setSelectedMST4010_R01(isSelected ? undefined : rowData);
	}, [selectedMST4010_R01, setSelectedMST4010_R01]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		equipmentCd: `${MST4010_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [MST4010_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [MST4010_R01List, selectedMST4010_R01]);

	return (
		<GridContextWrapper<MST4010_R01Dto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedMST4010_R01}
			defaultRowData={defaultMST4010_R01Row}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<MST4010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<MST4010_R01Dto>({
					idKey: 'equipmentCd',
					selectedList: selectedMST4010_R01 ? [selectedMST4010_R01.equipmentCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
